import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, CheckCircle2, Clock, DollarSign, MousePointer2, Users } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function StatCard({ label, value, sub, icon: Icon, testId }) {
    return (
        <div className="st-surface p-5" data-testid={testId}>
            <div className="flex items-center gap-2">
                {Icon && <Icon size={14} strokeWidth={1.6} color="var(--st-royal)" />}
                <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>{label}</div>
            </div>
            <div className="font-serif text-3xl mt-2" style={{ color: "var(--st-navy)" }}>{value}</div>
            {sub && <div className="text-xs mt-1" style={{ color: "var(--st-ink-muted)" }}>{sub}</div>}
        </div>
    );
}

function fmtDollars(cents) {
    const d = (cents || 0) / 100;
    return d.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function MonthlyChart({ series }) {
    const max = Math.max(1, ...series.map((s) => Math.max(s.clicks, s.conversions, s.earned_cents / 1000)));
    return (
        <div className="mt-5 flex items-end gap-1 h-32" data-testid="affiliate-monthly-chart">
            {series.map((row, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex items-end gap-0.5 h-24">
                        <div className="flex-1 rounded-t" style={{
                            background: "var(--st-silver-200, #C4CBDA)",
                            height: `${(row.clicks / max) * 100}%`,
                            minHeight: row.clicks > 0 ? "2px" : "0",
                        }} title={`${row.clicks} clicks`} />
                        <div className="flex-1 rounded-t" style={{
                            background: "var(--st-royal)",
                            height: `${(row.conversions / max) * 100}%`,
                            minHeight: row.conversions > 0 ? "2px" : "0",
                        }} title={`${row.conversions} conversions`} />
                    </div>
                    <div className="text-[9px] tracking-caps" style={{ color: "var(--st-silver-500)" }}>{row.month.slice(5)}</div>
                </div>
            ))}
        </div>
    );
}

export default function AffiliateDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [refs, setRefs] = useState([]);
    const [err, setErr] = useState(null);
    const [copied, setCopied] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsForm, setSettingsForm] = useState({ payout_handle: "", payout_method: "" });
    const [settingsNotice, setSettingsNotice] = useState(null);

    async function load() {
        try {
            const [d, r] = await Promise.all([api.affiliateDashboard(), api.affiliateReferrals()]);
            setData(d);
            setRefs(r.referrals);
            setSettingsForm({ payout_handle: d.payout_handle || "", payout_method: d.payout_method || "" });
        } catch (e) { setErr(e.message); }
    }
    useEffect(() => { load(); }, []);

    const refUrl = useMemo(() => data ? `${window.location.origin}/?ref=${data.ref_code}` : "", [data]);

    async function copyLink() {
        try {
            await navigator.clipboard.writeText(refUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* ignore */ }
    }

    async function saveSettings(e) {
        e.preventDefault();
        setSavingSettings(true);
        setSettingsNotice(null);
        try {
            await api.affiliateUpdateMe({
                payout_handle: settingsForm.payout_handle || null,
                payout_method: settingsForm.payout_method || null,
            });
            setSettingsNotice("Saved.");
            await load();
        } catch (ex) { setSettingsNotice(ex.message); }
        finally { setSavingSettings(false); }
    }

    if (err) return <PageShell><div className="p-8 text-sm" style={{ color: "#B42318" }} data-testid="affiliate-dashboard-error">{err}</div></PageShell>;
    if (!data) return <PageShell><div className="p-8 text-sm">Loading…</div></PageShell>;

    return (
        <PageShell>
            <section className="max-w-5xl mx-auto px-6 py-10 space-y-6" data-testid="affiliate-dashboard-page">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Affiliate</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>
                        Welcome, {user.name}.
                    </h1>
                </div>

                {/* Approval banner */}
                {!data.approved && (
                    <div className="rounded-lg p-4 flex items-start gap-3"
                         style={{ background: "#FFFAEB", color: "#7A2E0E", border: "1px solid #FEDF89" }}
                         data-testid="affiliate-pending-banner">
                        <Clock size={18} className="mt-0.5" />
                        <div className="text-sm leading-relaxed">
                            <strong>Your affiliate account is pending admin approval.</strong> You can share your ref link now — clicks are tracked — but commissions are only earned from approval onwards.
                        </div>
                    </div>
                )}

                {/* Payout method missing banner */}
                {(!data.payout_method || !data.payout_handle) && (
                    <div className="rounded-lg p-4 flex items-start justify-between gap-3"
                         style={{ background: "#FFFAEB", color: "#7A2E0E", border: "1px solid #FEDF89" }}
                         data-testid="payout-method-missing-banner">
                        <div className="text-sm leading-relaxed">
                            <strong>Add a payout method before your first payout can be processed.</strong>
                        </div>
                        <a href="#payout-settings" className="link-royal text-sm font-semibold shrink-0" data-testid="payout-setup-link">
                            Set up now →
                        </a>
                    </div>
                )}

                {/* Ref link card */}
                <div className="st-surface p-6" data-testid="affiliate-ref-link-card">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Your referral link</div>
                    <div className="mt-3 flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 px-4 py-3 rounded-md font-mono text-sm break-all"
                             style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}
                             data-testid="affiliate-ref-url">
                            {refUrl}
                        </div>
                        <button type="button" onClick={copyLink} className="btn-royal text-sm inline-flex items-center gap-2"
                                data-testid="affiliate-copy-btn">
                            {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                        </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        <span>Ref code: <span style={{ color: "var(--st-navy)", fontWeight: 600 }}>{data.ref_code}</span></span>
                        <span>·</span>
                        <span>Commission: <span style={{ color: "var(--st-navy)", fontWeight: 600 }}>{Math.round(data.commission_rate * 100)}%</span></span>
                        <span>·</span>
                        <span>Status: {data.approved
                            ? <span style={{ color: "#067647", fontWeight: 600 }} data-testid="affiliate-status-approved">Approved</span>
                            : <span style={{ color: "#B54708", fontWeight: 600 }} data-testid="affiliate-status-pending">Pending approval</span>}
                        </span>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <StatCard label="Lifetime clicks" value={data.lifetime_clicks} icon={MousePointer2} testId="stat-clicks" />
                    <StatCard label="Signups" value={data.lifetime_signups} sub={`${data.lifetime_conversions} converted`} icon={Users} testId="stat-signups" />
                    <StatCard label="Lifetime earned" value={fmtDollars(data.lifetime_earned_cents)} icon={DollarSign} testId="stat-earned" />
                    <StatCard label="Paid out" value={fmtDollars(data.lifetime_paid_cents)} testId="stat-paid" />
                    <StatCard label="Pending payout" value={fmtDollars(data.pending_payout_cents)} testId="stat-pending" />
                    <StatCard label="Commission rate" value={`${Math.round(data.commission_rate * 100)}%`} testId="stat-rate" />
                </div>

                {/* Chart */}
                <div className="st-surface p-6" data-testid="affiliate-chart-card">
                    <div className="flex items-center justify-between">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Last 12 months</div>
                        <div className="flex gap-3 text-[10px] tracking-caps" style={{ color: "var(--st-silver-500)" }}>
                            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "var(--st-silver-200, #C4CBDA)" }} />Clicks</span>
                            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: "var(--st-royal)" }} />Conversions</span>
                        </div>
                    </div>
                    <MonthlyChart series={data.monthly_time_series} />
                </div>

                {/* Recent referrals */}
                <div className="st-surface p-6" data-testid="affiliate-referrals-card">
                    <div className="flex items-center justify-between">
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Recent referrals</div>
                        <Link to="/affiliate/commissions" className="link-royal text-xs">View commissions →</Link>
                    </div>
                    {refs.length === 0 ? (
                        <p className="mt-4 text-sm italic" style={{ color: "var(--st-silver-500)" }} data-testid="affiliate-refs-empty">
                            No referrals yet. Share your link.
                        </p>
                    ) : (
                        <table className="w-full text-sm mt-4">
                            <thead>
                                <tr style={{ color: "var(--st-ink-muted)" }}>
                                    <th className="text-left py-2 tracking-caps text-[10px] font-semibold">Referred</th>
                                    <th className="text-left py-2 tracking-caps text-[10px] font-semibold">Attributed</th>
                                    <th className="text-left py-2 tracking-caps text-[10px] font-semibold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {refs.slice(0, 8).map((r) => (
                                    <tr key={r.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`ref-row-${r.id}`}>
                                        <td className="py-3">{r.referred_email_masked}</td>
                                        <td className="py-3 text-xs" style={{ color: "var(--st-ink-muted)" }}>{new Date(r.attributed_at).toLocaleDateString()}</td>
                                        <td className="py-3 text-xs tracking-caps font-semibold">{r.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Payout settings */}
                <form onSubmit={saveSettings} className="st-surface p-6" data-testid="affiliate-payout-settings" id="payout-settings">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Payout settings</div>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        We do not process payouts automatically. When a payout is issued, we send it to the handle below.
                    </p>
                    {(() => {
                        const methodLabels = {
                            paypal:  { label: "PayPal email",              placeholder: "you@example.com" },
                            zelle:   { label: "Zelle email or phone",      placeholder: "you@example.com or +15551234567" },
                            cashapp: { label: "Cash App $cashtag",         placeholder: "$YourCashtag" },
                            venmo:   { label: "Venmo @username",           placeholder: "@yourhandle" },
                        };
                        const cur = methodLabels[settingsForm.payout_method] || { label: "Handle", placeholder: "Select a method first" };
                        return (
                            <div className="mt-4 grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="st-label">Payout method</label>
                                    <select className="st-input" value={settingsForm.payout_method}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, payout_method: e.target.value })}
                                            data-testid="settings-payout-method">
                                        <option value="">Select…</option>
                                        <option value="paypal">PayPal</option>
                                        <option value="zelle">Zelle</option>
                                        <option value="cashapp">Cash App</option>
                                        <option value="venmo">Venmo</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="st-label">{cur.label}</label>
                                    <input className="st-input"
                                           value={settingsForm.payout_handle}
                                           onChange={(e) => setSettingsForm({ ...settingsForm, payout_handle: e.target.value })}
                                           placeholder={cur.placeholder}
                                           disabled={!settingsForm.payout_method}
                                           data-testid="settings-payout-handle" />
                                </div>
                            </div>
                        );
                    })()}
                    <div className="mt-4 flex items-center gap-3">
                        <button type="submit" className="btn-royal text-sm" disabled={savingSettings} data-testid="settings-save">
                            {savingSettings ? "Saving…" : "Save settings"}
                        </button>
                        {settingsNotice && <span className="text-xs" style={{ color: "var(--st-ink-muted)" }} data-testid="settings-notice">{settingsNotice}</span>}
                    </div>
                </form>
            </section>
        </PageShell>
    );
}
